import React, { useState, useEffect, Suspense, useCallback } from "react";
import { useDispatch } from "react-redux";
import { format, addYears, parseISO, addDays, addMonths } from "date-fns";
import { apiService } from "../../api/apiAdapter";
import { updateSingleCart, updateMultiCart } from "../../store/reducers/subscripion";
import { useNavigate } from "react-router-dom";
import { handleDeleteAction } from "./cartUtils";
import useAuth from "../../hooks/useAuth";
import styles from "./myCart.module.scss";
import { usePromotion } from "../../hooks/usePromotion";

const NavBar = React.lazy(() => import("../../components/navBar"));
const TopicBanner = React.lazy(() => import("../../components/SubscribeTopicBanner"));
const Footer = React.lazy(() => import("../../components/footer"));

const isWithinRenewalWindow = (expiryDate) => {
  const today = new Date();
  const exp = new Date(expiryDate);

  // Define window: 3 months before to 1 month after
  const windowStart = addDays(addYears(addMonths(exp, -3), 0), 0); // 3 months prior
  const windowEnd = addMonths(exp, 1); // 1 month after

  return today >= windowStart && today <= windowEnd;
};

const subscriptionAppMap = {
  151: "tg",
  170: "tg",
  2151: "obesid",
  2170: "obesid",
  3151: "drtc",
  3170: "drtc",
  4151: "aidh",
  4170: "aidh",
};

const MyCart = () => {
  const navigate = useNavigate();
  const handleBack = () => {
    const selectedApps = [
      ...new Set(
        cartItems.map((item) => subscriptionAppMap[String(item.subscriptionid)]).filter(Boolean),
      ),
    ];

    navigate("/subscribe", {
      state: {
        subscribeStep: 3,
        subscribeSelections: {
          selectedApps,
          purchaseOption: "singleUser",
        },
      },
    });
  };

  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();
  const [cartItems, setCartItems] = useState([]);
  const { email, subscriptionDetails } = useAuth();
  // Promotions — called unconditionally (Rules of Hooks)
  const promoTGSingle = usePromotion("151");
  const promoTGMulti = usePromotion("170");
  const promoObesIDSingle = usePromotion("2151");
  const promoObesIDMulti = usePromotion("2170");
  const promoDRTCSingle = usePromotion("3151");
  const promoDRTCMulti = usePromotion("3170");
  const promoAIDHSingle = usePromotion("4151");
  const promoAIDHMulti = usePromotion("4170");
  const promoBySubscriptionId = {
    151: promoTGSingle,
    170: promoTGMulti,
    2151: promoObesIDSingle,
    2170: promoObesIDMulti,
    3151: promoDRTCSingle,
    3170: promoDRTCMulti,
    4151: promoAIDHSingle,
    4170: promoAIDHMulti,
  };

  const fetchCart = useCallback(async () => {
    setIsLoading(true);
    try {
      const items = await apiService.getCart();
      console.log("Fetched Cart Items:", items);

      if (!items || items.length === 0) {
        setCartItems([]);
        return;
      }

      // Process and enrich every item in the cart
      const enrichedItems = items.map((item) => {
        const { description, startDate, endDate } = handleSubscriptionDatesAndDescription(
          item.ordertype,
          item.currentsubscriptionenddate,
          item.subscriptionid,
        );

        // Update Redux state for each item based on type
        if (["151", "2151", "3151", "4151"].includes(item.subscriptionid)) {
          dispatch(
            updateSingleCart({
              cartsId: item.cartsid,
              quantity: item.quantity,
            }),
          );
        } else if (["170", "2170", "3170", "4170"].includes(item.subscriptionid)) {
          dispatch(updateMultiCart({ cartsId: item.cartsid, quantity: item.quantity }));
        }

        return {
          ...item,
          startDate,
          endDate,
          description,
        };
      });

      setCartItems(enrichedItems);
    } catch (error) {
      console.error("Failed to fetch cart:", error);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const getPriceFromEnv = (subId, ordertype) => {
    const isRenew = ordertype === "Renew";

    if (["151", "170"].includes(subId)) {
      return isRenew
        ? parseFloat(import.meta.env.VITE_TG_SINGLE_USER_RENEW_PRICE || 0)
        : parseFloat(import.meta.env.VITE_TG_SINGLE_USER_PRICE || 0);
    }
    if (["2151", "2170"].includes(subId)) {
      return isRenew
        ? parseFloat(import.meta.env.VITE_OB_SINGLE_USER_RENEW_PRICE || 0)
        : parseFloat(import.meta.env.VITE_OB_SINGLE_USER_PRICE || 0);
    }
    if (["3151", "3170"].includes(subId)) {
      return isRenew
        ? parseFloat(import.meta.env.VITE_DRTC_SINGLE_USER_RENEW_PRICE || 0)
        : parseFloat(import.meta.env.VITE_DRTC_SINGLE_USER_PRICE || 0);
    }
    if (["4151", "4170"].includes(subId)) {
      return isRenew
        ? parseFloat(import.meta.env.VITE_AIDH_SINGLE_USER_RENEW_PRICE || 0)
        : parseFloat(import.meta.env.VITE_AIDH_SINGLE_USER_PRICE || 0);
    }
    return 0;
  };

  const calculateGrandTotal = () => {
    return cartItems.reduce((acc, item) => acc + getDisplayPrice(item), 0);
  };

  const goToCheckoutPage = async () => {
    if (!email) {
      navigate("/userConfirmation", { state: { cartItems } });
      return;
    }

    if (subscriptionDetails?.isMultipleUser) {
      alert("Eligibility issue: Multi-user subscription holders cannot make purchases.");
      return;
    }

    let userProfile = null;
    try {
      userProfile = await apiService.getUserProfile(email, true, {
        forceRefresh: true,
      });
    } catch (error) {
      console.warn("Error fetching profile before checkout eligibility check", error);
      alert("Error checking eligibility.");
      return;
    }

    for (const item of cartItems) {
      const { allowed, message, canConvertToRenew, existingSubDate, entitlementId, shouldRemove } =
        await checkUserCartEligibility(item, userProfile);

      if (!allowed) {
        if (shouldRemove) {
          // CASE: Outside renewal window
          const confirmRemove = window.confirm(
            `${message}\n\nThis item cannot be purchased. Would you like to remove it from your cart?`,
          );

          if (confirmRemove) {
            // Determine type for Redux
            const type = ["151", "2151", "3151", "4151"].includes(item.subscriptionid)
              ? "single"
              : "multiple";

            // Call the utility
            await handleDeleteAction(item.cartsid, type, dispatch);
            fetchCart();
          }
        } else if (canConvertToRenew) {
          const confirmRenew = window.confirm(
            `${message}\n\nWould you like to update this item to a 'Renewal' instead?`,
          );

          if (confirmRenew) {
            try {
              // Update the item to 'Renew' and attach the date
              await apiService.updateCartItem({
                cartsid: item.cartsid,
                quantity: item.quantity,
                ordertype: "Renew",
                currentSubscriptionId: entitlementId,
                currentsubscriptionenddate: existingSubDate,
              });

              // Refresh the cart so descriptions/dates recalculate
              await fetchCart();
              return; // Stop execution so user can see updated cart before clicking checkout again
            } catch (err) {
              alert("Failed to update cart. Please try again.", err);
              return;
            }
          }
        } else {
          alert(`Eligibility issue: ${message}`);
        }
        return;
      }
    }

    navigate("/checkout", { state: { cartItems } });
  };
  const handleSubscriptionDatesAndDescription = (
    ordertype,
    currentsubscriptionenddate,
    subscriptionid,
  ) => {
    const nameMap = {
      151: "Therapeutic Guidelines",
      170: "Therapeutic Guidelines (Multi)",
      2151: "ObesiD",
      2170: "ObesiD (Multi)",
      3151: "DRTC",
      3170: "DRTC (Multi)",
      4151: "AIDH",
      4170: "AIDH (Multi)",
    };
    const pubName = nameMap[subscriptionid] || "Subscription";
    const baseText = `${pubName} 12 month subscription`;

    const formatToMidnightUTC = (date) => {
      const d = new Date(date);
      d.setUTCHours(0, 0, 0, 0);
      return d.toISOString().replace(/\.000Z$/, "Z");
    };

    try {
      switch (ordertype) {
        case "New": {
          const today = new Date();
          return {
            description: `${baseText} (expires ${format(addYears(today, 1), "dd-MMM-yyyy")})`,
            startDate: formatToMidnightUTC(today),
            endDate: formatToMidnightUTC(addYears(today, 1)),
          };
        }
        case "Renew": {
          const today = new Date();
          const currentEndDate = parseISO(currentsubscriptionenddate);
          const isExpired =
            new Date(today).setHours(0, 0, 0, 0) > new Date(currentEndDate).setHours(0, 0, 0, 0);

          if (isExpired) {
            return {
              description: `${baseText} renewal (expires ${format(
                addYears(today, 1),
                "dd-MMM-yyyy",
              )})`,
              startDate: formatToMidnightUTC(today),
              endDate: formatToMidnightUTC(addYears(today, 1)),
            };
          }

          const renewalStart = currentEndDate;
          return {
            description: `${baseText} renewal (expires ${format(addYears(renewalStart, 1), "dd-MMM-yyyy")})`,
            startDate: formatToMidnightUTC(renewalStart),
            endDate: formatToMidnightUTC(addYears(renewalStart, 1)),
          };
        }
        case "Addon": {
          const endDate = parseISO(currentsubscriptionenddate);
          const startDate = addYears(endDate, -1);
          return {
            description: `${baseText} additional licenses (expires ${format(endDate, "dd-MMM-yyyy")})`,
            startDate: formatToMidnightUTC(startDate),
            endDate: formatToMidnightUTC(endDate),
          };
        }
        default:
          return { description: baseText, startDate: null, endDate: null };
      }
    } catch (error) {
      console.warn("Error handling Subscription Dates And Description", error);
      return { description: baseText, startDate: null, endDate: null };
    }
  };

  const getDisplayPrice = (item) => {
    const activePromo = promoBySubscriptionId[String(item.subscriptionid)];
    if (activePromo?.discountType === "fixed" && activePromo?.price) {
      return activePromo.price;
    }

    const apiPrice = ["151", "2151", "3151", "4151"].includes(item.subscriptionid)
      ? item.individualprice
      : item.multipleprice;

    if (apiPrice && apiPrice > 0) return apiPrice;

    // Fallback logic
    return getPriceFromEnv(item.subscriptionid, item.ordertype);
  };

  async function checkUserCartEligibility(item, profileResponse) {
    try {
      if (subscriptionDetails?.isMultipleUser) {
        return {
          allowed: false,
          message: "Multi-user subscription holders cannot make purchases.",
        };
      }

      const response = profileResponse;

      const { contact } = response.data;
      const activeSubs = Array.isArray(response?.data?.subscriptions)
        ? response.data.subscriptions
        : response?.data?.subscriptions
          ? [response.data.subscriptions]
          : subscriptionDetails?.allSubscriptions || [];

      if (contact?._tg_parentcontactid_value) {
        return {
          allowed: false,
          message: "Sub-accounts cannot make purchases.",
        };
      }

      const existingSub = activeSubs.find(
        (sub) => String(sub.tg_subscriptionid) === String(item.subscriptionid),
      );
      if (existingSub) {
        const expiryDate = new Date(existingSub.tg_expirydate);
        const isWindowOpen = isWithinRenewalWindow(expiryDate);
        const formattedExpiry = existingSub["tg_expirydate"];
        const subscriptionName =
          existingSub["tg_subscriptionid@OData.Community.Display.V1.FormattedValue"] ||
          item.description ||
          `subscription ${item.subscriptionid}`;

        // Determine if the subscription is "Long Expired"
        const isLongExpired = new Date() > addMonths(expiryDate, 1);

        // ---  USER ADDING 'NEW' ---
        if (item.ordertype === "New") {
          // If they are in the renewal window, they MUST renew
          if (isWindowOpen) {
            return {
              allowed: false,
              canConvertToRenew: true,
              existingSubDate: existingSub.tg_expirydate,
              entitlementId: existingSub.entitlementid,
              message: `You have an active subscription for ${subscriptionName} (expires ${formattedExpiry}). This should be processed as a Renewal.`,
            };
          }

          // If it's NOT long expired and NOT in window (it's still active and far from expiry)
          if (!isLongExpired) {
            return {
              allowed: false,
              shouldRemove: true,
              message: `You already have an active subscription for ${subscriptionName} until ${formattedExpiry}.`,
            };
          }

          // If isLongExpired is true, we allow 'New' to proceed (return allowed: true at end)
        }

        // ---  USER ADDING 'RENEW' ---
        if (item.ordertype === "Renew" && !isWindowOpen) {
          return {
            allowed: false,
            shouldRemove: true,
            message: `Renewals are only available from 3 months before to 1 month after expiry (${formattedExpiry}).`,
          };
        }
      }

      return { allowed: true, message: "" };
    } catch (e) {
      console.warn("Error checking user cart eligibility", e);
      return { allowed: false, message: "Error checking eligibility." };
    }
  }

  const onDeleteItem = async (cartsid, subid) => {
    const type = ["151", "2151", "3151", "4151"].includes(subid) ? "single" : "multiple";
    await handleDeleteAction(cartsid, type, dispatch);
    fetchCart();
  };

  return (
    <Suspense fallback={<div className="loader"></div>}>
      <NavBar />
      <TopicBanner />

      {isLoading ? (
        <div className="text-center">
          <div className="spinner-border" role="status"></div>
          <p>Loading your cart items...</p>
        </div>
      ) : cartItems.length === 0 ? (
        <div className={styles.myCart}>
          <div className={styles.backBtn}>
            <button type="button" className={styles.backLink} onClick={handleBack}>
              <i className="tg-icons-RightArrow" /> Back
            </button>
          </div>
          <div className="empty-cart-message">No items found in your cart</div>
        </div>
      ) : (
        <div className={styles.myCart}>
          <div className={styles.backBtn}>
            <button type="button" className={styles.backLink} onClick={handleBack}>
              <i className="tg-icons-RightArrow" /> Back
            </button>
          </div>
          <h4 className={styles.sectionHeading}>My purchases</h4>

          <table className={styles.myPurchases}>
            <thead className={styles.tableHeader}>
              <tr>
                <th>Product</th>
                <th>Quantity</th>
                <th>Total price (AUD, incl GST)</th>
                <th style={{ backgroundColor: "#ecf1f2" }}></th>
              </tr>
            </thead>
            <tbody className={styles.tableBody}>
              {cartItems.map((item) => (
                <tr key={item.cartsid}>
                  <td className={styles.product}>{item.description}</td>
                  <td className={styles.quantityCell}>{item.quantity}</td>
                  <td className={styles.priceCell}>$ {getDisplayPrice(item).toFixed(2)}</td>
                  <td
                    className={styles.deleteIcon}
                    onClick={() => onDeleteItem(item.cartsid, item.subscriptionid)}
                  >
                    <i className="fa fa-trash-o font20px"></i>
                  </td>
                </tr>
              ))}
            </tbody>

            <tfoot>
              <tr className={styles.totalRow}>
                <td colSpan="2" className={styles.totalLabel}>
                  Grand Total :
                </td>
                <td colSpan="2">
                  {calculateGrandTotal().toLocaleString("en-AU", {
                    style: "currency",
                    currency: "AUD",
                  })}
                </td>
              </tr>
            </tfoot>
          </table>

          <div className={styles.buttonGroup}>
            <button className={styles.cartActions} onClick={() => navigate("/subscribe")}>
              Continue shopping
            </button>
            <button className={styles.checkoutBtn} onClick={goToCheckoutPage}>
              Proceed to checkout
            </button>
          </div>
        </div>
      )}
      <Footer />
    </Suspense>
  );
};

export default MyCart;
